import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTransactions, useTracking, useUI } from '../../hooks';
import { Transaction, Tracking } from '../../types';
import { getTrackingConfig } from '../../config';
import Text from '../Text/Text';
import StarRating from '../StarRating/StarRating';
import { CountdownTimer } from './CountdownTimer';
import { ReleaseConfirmationModal } from './ReleaseConfirmationModal';
import './ReleaseFunds.css';
import LabeledValue from '../LabeledValue';
import HelpTooltip from '../HelpTooltip';

interface ReleaseFundsProps {
  transactionId: string;
}

/**
 * ReleaseFunds component
 *
 * This component displays transaction details and provides functionality to release funds.
 * It shows:
 * - Transaction details (item description, amount)
 * - Tracking information (number, status)
 * - Funds deposit date
 * - Seller rating
 * - Release funds button
 * - Countdown timer for automatic release (configurable via environment variables)
 */
const ReleaseFundsComponent: React.FC<ReleaseFundsProps> = ({
  transactionId
}) => {
  const navigate = useNavigate();
  const { getTransaction, releasePayment } = useTransactions();
  const { getTrackingInfo } = useTracking();
  const { showToast } = useUI();
  
  const [transaction, setTransaction] = useState<Transaction | null>(null);
  const [tracking, setTracking] = useState<Tracking | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<Error | null>(null);
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [trackingError, setTrackingError] = useState<{ code: number; message: string } | null>(null);
  const [sellerRating, setSellerRating] = useState<number>(1); // Add state for seller rating
  const [retryCount, setRetryCount] = useState<number>(0);
  const MAX_RETRY_ATTEMPTS = 3;
  
  // Ref to track component mount status
  const isMountedRef = useRef(true);
  // Ref to track if data has been fetched
  const dataFetchedRef = useRef(false);
  // Ref to store the last transaction ID to prevent unnecessary refetches
  const lastTransactionIdRef = useRef<string | null>(null);
  
  // Dedicated effect for handling component unmounting
  useEffect(() => {
    // Set isMountedRef to true when the component mounts
    isMountedRef.current = true;
    console.log('[DEBUG] ReleaseFunds: Component mounted, setting isMountedRef to true');
    
    // Return cleanup function that will only run when the component is actually unmounting
    return () => {
      console.log('[DEBUG] ReleaseFunds: Component unmounting (from dedicated unmount effect)');
      isMountedRef.current = false;
    };
  }, []); // Empty dependency array ensures this only runs on mount/unmount
  
  // Fetch transaction and tracking data
  useEffect(() => {
    console.log(`[DEBUG] ReleaseFunds: Effect triggered with transactionId: ${transactionId}, retry count: ${retryCount}, dataFetched: ${dataFetchedRef.current}`);
    
    // Skip effect if no transactionId is provided
    if (!transactionId) {
      console.log('[DEBUG] ReleaseFunds: No transactionId provided, skipping effect');
      return;
    }
    
    // Skip if we've already fetched data for this transaction ID (except for retries)
    if (dataFetchedRef.current && lastTransactionIdRef.current === transactionId && retryCount === 0) {
      console.log('[DEBUG] ReleaseFunds: Data already fetched for this transaction ID and no retry requested, skipping effect');
      return;
    }
    
    // Update the last transaction ID ref
    lastTransactionIdRef.current = transactionId;
    
    console.log(`[DEBUG] ReleaseFunds: Starting data fetch for transactionId: ${transactionId}, retry count: ${retryCount}`);
    
    // Reset the mounted ref to true at the start of the effect
    isMountedRef.current = true;
    console.log('[DEBUG] ReleaseFunds: Setting isMountedRef to true at effect start');
    
    const fetchData = async () => {
      try {
        console.log('[DEBUG] ReleaseFunds: Checking if component is still mounted:', isMountedRef.current);
        if (isMountedRef.current) {
          setIsLoading(true);
          setError(null);
          setTrackingError(null);
        }
        
        console.log(`[DEBUG] ReleaseFunds: Fetching transaction data for ID: ${transactionId}`);
        // Mark data as being fetched
        dataFetchedRef.current = true;
        // Fetch transaction details
        const transactionData = await getTransaction(transactionId);
        console.log(`[DEBUG] ReleaseFunds: Transaction data received:`, transactionData);
        
        // Only update state if component is still mounted and transaction data is valid
        console.log('[DEBUG] ReleaseFunds: Before setting transaction state:', {
          isMounted: isMountedRef.current,
          hasTransactionData: !!transactionData,
          transactionDataType: typeof transactionData,
          currentTransactionState: transaction // Log current transaction state before update
        });
        
        if (isMountedRef.current && transactionData && typeof transactionData === 'object') {
          console.log('[DEBUG] ReleaseFunds: Setting transaction state with data:', transactionData);
          // Use functional update to ensure we're working with the latest state
          setTransaction(prevTransaction => {
            console.log('[DEBUG] ReleaseFunds: Previous transaction state in functional update:', prevTransaction);
            return transactionData;
          });
          console.log('[DEBUG] ReleaseFunds: Transaction state update requested');
          
          // Add a timeout to check if the state was actually updated
          setTimeout(() => {
            console.log('[DEBUG] ReleaseFunds: Checking transaction state after update:', {
              hasTransaction: !!transaction,
              transactionId: transaction?.id,
              transactionStatus: transaction?.status,
              transactionAmount: transaction?.totalAmount,
              transactionItemDescription: transaction?.itemDescription,
              transactionTrackingId: transaction?.trackingId
            });
          }, 0);
          
          console.log(`[DEBUG] ReleaseFunds: Tracking id received:`, transactionData.trackingId);
          // Fetch tracking information if available
          if (transactionData.trackingId) {
            console.log(`[DEBUG] ReleaseFunds: Fetching tracking data for ID: ${transactionData.trackingId}`);
            try {
              const trackingData = await getTrackingInfo(transactionData.trackingId);
              console.log(`[DEBUG] ReleaseFunds: Tracking data received:`, trackingData);
              
              if (isMountedRef.current) {
                setTracking(trackingData);
                // Reset retry count on success
                setRetryCount(0);
              }
            } catch (trackingErr) {
              // Only update state if component is still mounted
              if (isMountedRef.current) {
                // Handle tracking-specific errors
                const error = trackingErr as Error;
                console.error(`[DEBUG] ReleaseFunds: Tracking data fetch error: ${error.message}`);
                
                // Check if it's a 404 error (not found)
                const is404 = error.message.includes('404') || 
                              error.message.toLowerCase().includes('not found');
                
                setTrackingError({
                  code: is404 ? 404 : 500,
                  message: is404 ? 'Tracking information not available' : 'Failed to load tracking information'
                });
                
                // Show toast for tracking error
                showToast('warning', is404 ? 
                  'Tracking information is not available. Funds cannot be released.' : 
                  'Failed to load tracking information. Please try again later.');
                
                // Set tracking to null to ensure UI shows "Not Available"
                setTracking(null);
              }
            }
          } else {
            console.log(`[DEBUG] ReleaseFunds: No tracking ID found in transaction data`);
          }
        }
      } catch (err) {
        // Only update state if component is still mounted
        if (isMountedRef.current) {
          const error = err as Error;
          console.error(`[DEBUG] ReleaseFunds: Transaction fetch error: ${error.message}`);
          setError(error);
          showToast('error', 'Failed to load transaction details');
        }
      } finally {
        // Only update state if component is still mounted
        if (isMountedRef.current) {
          setIsLoading(false);
        }
      }
    };
    
    fetchData();
    
    // Cleanup function for data fetching effect
    return () => {
      console.log(`[DEBUG] ReleaseFunds: Data fetching effect cleanup for transactionId: ${transactionId}`);
      // We don't need to set isMountedRef here anymore since we have a dedicated unmount effect
    };
  }, [transactionId, getTransaction, getTrackingInfo, showToast, retryCount, transaction]);
  
  // Handle retry for tracking data
  const handleRetryTracking = useCallback(() => {
    if (retryCount < MAX_RETRY_ATTEMPTS) {
      console.log(`[DEBUG] ReleaseFunds: Retrying tracking data fetch. Current count: ${retryCount}, Max: ${MAX_RETRY_ATTEMPTS}`);
      
      // Increment retry count to trigger useEffect
      setRetryCount(prevCount => {
        const nextCount = prevCount + 1;
        console.log(`[DEBUG] ReleaseFunds: Incrementing retry count from ${prevCount} to ${nextCount}`);
        return nextCount;
      });
      
      showToast('info', `Retrying to fetch tracking information (Attempt ${retryCount + 1}/${MAX_RETRY_ATTEMPTS})...`);
    } else {
      showToast('error', 'Maximum retry attempts reached. Please try again later.');
    }
  }, [retryCount, showToast, MAX_RETRY_ATTEMPTS]);
  
  // Handle release funds button click
  const handleReleaseFundsClick = useCallback(() => {
    setIsModalOpen(true);
  }, []);
  
  // Handle modal close
  const handleModalClose = useCallback(() => {
    setIsModalOpen(false);
  }, []);
  
  // Handle release confirmation
  const handleReleaseConfirm = useCallback(async () => {
    if (!transaction) return;
    
    try {
      // Use override if provided
      const releaseFunds = releasePayment;
      await releaseFunds(transaction.id, sellerRating);
      showToast('success', 'Funds released successfully!');
      navigate('/transactions');
    } catch (error) {
      // Error handling is managed by the modal component
      throw error;
    }
  }, [transaction, releasePayment, showToast, navigate, sellerRating]);
  

  // Update stable state when the actual state changes
  useEffect(() => {
    // Only update if we have meaningful changes
    console.log('[DEBUG] ReleaseFunds: Checking stable state update conditions:', {
      isLoading,
      hasTransaction: !!transaction,
      hasError: !!error,
      transaction,
      tracking,
      trackingError
    });
    
    // Always update stableState when transaction data changes, regardless of isLoading
    if (transaction || error) {
      console.log('[DEBUG] ReleaseFunds: Updating stable state with transaction:', transaction);
      
      // Log transaction properties to help diagnose issues
      if (transaction) {
        console.log('[DEBUG] ReleaseFunds: Transaction properties:', {
          id: transaction.id,
          status: transaction.status,
          amount: transaction.totalAmount,
          itemId: transaction.itemId,
          itemDescription: transaction.itemDescription,
          trackingId: transaction.trackingId,
          createdAt: transaction.createdAt
        });
      }
    } else {
      console.log('[DEBUG] ReleaseFunds: NOT updating stable state due to conditions not met');
    }
    
    // Log tracking information
    if (tracking) {
      console.log('[DEBUG] ReleaseFunds: Tracking information:', {
        id: tracking.id,
        status: tracking.status,
        trackingNumber: tracking.trackingNumber,
        carrier: tracking.carrier,
        statusHistory: tracking.statusHistory?.length
      });
    }
    
    // Log the canReleaseFunds condition
    const terminalStatuses = getTrackingConfig()?.terminalStatuses || [];
    console.log('[DEBUG] ReleaseFunds: Can release funds condition:', {
      hasTransaction: !!transaction,
      transactionStatus: transaction?.status,
      hasTracking: !!tracking,
      trackingStatus: tracking?.status,
      terminalStatuses,
      isTrackingStatusTerminal: tracking ? terminalStatuses.includes(tracking.status) : false,
      hasTrackingError: !!trackingError
    });
    
  }, [isLoading, error, transaction, tracking, trackingError]);

  // Add a dedicated effect to monitor transaction state changes
  useEffect(() => {
    console.log('[DEBUG] ReleaseFunds: Transaction state changed:', {
      hasTransaction: !!transaction,
      transactionId: transaction?.id,
      transactionStatus: transaction?.status,
      transactionAmount: transaction?.totalAmount,
      transactionItemDescription: transaction?.itemDescription,
      transactionTrackingId: transaction?.trackingId
    });
  }, [transaction]); // Only depend on transaction state

  // Get automatic release time from transaction or calculate it
  const getAutoReleaseTime = () => {
    if (!transaction) return null;

    console.log('[DEBUG] Transaction Object: ', transaction);
    
    const autoReleaseTime = transaction.autoReleaseTime ? new Date(transaction.autoReleaseTime) : null;
    console.log(`[DEBUG] ReleaseFunds: auto-release time: ${autoReleaseTime}`);
    
    return autoReleaseTime;
  };
  
  const autoReleaseTime = getAutoReleaseTime();
  
  // Check if auto-release timer has expired
  const isAutoReleaseExpired = autoReleaseTime ? new Date().getTime() >= autoReleaseTime.getTime() : false;
  
  // Check if funds can be released
  const terminalStatuses = getTrackingConfig()?.terminalStatuses || [];
  const canReleaseFunds = transaction &&
    transaction.status === 'in_escrow' &&
    tracking &&
    terminalStatuses.includes(tracking.status) &&
    !trackingError &&
    !isAutoReleaseExpired; // Don't allow manual release if auto-release has already occurred
  
  // Log transaction state before rendering
  console.log('[DEBUG] ReleaseFunds: Before rendering, transaction state:', {
    hasTransaction: !!transaction,
    transactionId: transaction?.id,
    transactionStatus: transaction?.status,
    transactionAmount: transaction?.totalAmount,
    transactionItemDescription: transaction?.itemDescription,
    transactionTrackingId: transaction?.trackingId
  });
  
  return (
    <div className="release-funds-container">
      {/* First Card: Transaction Details */}
      <div className="release-funds-card">
        <Text className="text-h2">Release Funds</Text>
        
        <div className="transaction-details">
          <Text className="text-body"><span className='text-body-bold'>Item Description:</span> {transaction ? transaction.itemDescription : 'Loading...'}</Text>
          <Text className="text-body-bold">Amount to be Released: ${transaction && transaction.totalAmount ? transaction.totalAmount.toFixed(2) : '0.00'}</Text>
          
          <div className="tracking-info">
            {tracking ? (
              <>
                <div className="tracking-number">
                  <LabeledValue label="Tracking Number" value={transaction && transaction.trackingId ? tracking.trackingNumber : 'Not Available'} multiline={true} copyable={true} />                  
                </div>
                <Text
                    className="text-primary text-link"
                    isLink
                    onClick={() => navigate(`/tracking/${tracking.id}`)}
                  >
                  Tracking History
                </Text>
                <Text className="text-body">
                  <span className="text-body-bold">Last Tracking Status:</span>{tracking.status}
                </Text>
              </>
            ) : (
              <>
                <div className="tracking-number">
                  <Text className="text-body-bold">
                    <span className="text-body-bold">Tracking Number:</span> <span className="not-available">{tracking}</span>
                  </Text>
                </div>
                <Text className="text-body">
                  <span className="text-body-bold">Last Tracking Status: </span><span className="not-available">Not Available</span>
                </Text>
                {trackingError && (
                  <div className="tracking-error-message">
                    <Text className="text-warning">
                      {trackingError.message}
                    </Text>
                    {retryCount < MAX_RETRY_ATTEMPTS && (
                      <button
                        className="retry-button small"
                        onClick={handleRetryTracking}
                      >
                        Retry ({retryCount}/{MAX_RETRY_ATTEMPTS})
                      </button>
                    )}
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>

      {/* Second Card: Funds Deposited */}
      <div className="release-funds-card">
        <div className="funds-info">
          <Text className="text-body">
            <span className='text-body-bold'>Funds Deposited:</span> {transaction?.createdAt ? new Date(transaction.createdAt).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric'
          }) : 'Not available'}</Text>
        </div>
        <Text
            className="text-primary text-link"
            isLink
            onClick={() => transaction ? navigate(`/payment/${transaction.id}`) : showToast('error', 'Transaction details not available')}
          >
            View Receipt
        </Text>
      </div>
      
      {/* Third Card: Seller Rating */}
      <div className="release-funds-card">
        <div className="seller-rating">
          <div className="seller-rating-label">
            <Text className="text-body">Rate this Seller:</Text>
            <HelpTooltip
              content="Your rating helps other buyers make informed decisions about this seller. A 5-star rating indicates excellent service, while a 1-star rating suggests significant issues. Ratings contribute to the seller's overall reputation score."
              position="top"
              ariaLabel="Seller rating information"
            />
          </div>
          <StarRating
            rating={sellerRating}
            onChange={(newRating) => {
              setSellerRating(newRating);
              showToast('info', `Rating set to ${newRating} stars`);
            }}
          />
        </div>
      </div>
      
      {/* Release Funds Button */}
      <button
        className="release-funds-button purple-button"
        onClick={handleReleaseFundsClick}
        disabled={!canReleaseFunds}
      >
        Release Funds Now
      </button>
      
      {/* Tracking Error Notification */}
      {trackingError && (
        <div className="tracking-notification">
          <Text className="text-warning text-center">
            Funds cannot be released without valid tracking information.
          </Text>
        </div>
      )}
      
      {/* Automatic Release Timer */}
      {autoReleaseTime && (
        <div className="auto-release-info">
          <Text className="text-body text-center">Funds will be automatically released in:</Text>
          <CountdownTimer targetDate={autoReleaseTime} expiredText={`Funds have been automatically released.`} />
        </div>
      )}
      
      {/* Confirmation Modal */}
      {isModalOpen && transaction && (
        <ReleaseConfirmationModal
          isOpen={isModalOpen}
          onClose={handleModalClose}
          onConfirm={handleReleaseConfirm}
          transactionDetails={{
            id: transaction.id,
            totalAmount: transaction.totalAmount,
            sellerUserId: transaction.sellerPublicId, // In a real app, we would fetch the seller's name
            itemDescription: transaction.itemDescription || transaction.itemId || 'Item' // Fallback to itemId if itemDescription is not available
          }}
        />
      )}
    </div>
  );
};

export default ReleaseFundsComponent;